// Content of the info window shown when a site pin is clicked on the Live Map
import { JobDetailsLink, PopupJobRow } from '@/components/LiveMap/JobSelection';
import { SiteMarkerData } from '@/components/LiveMap/LiveMapMarkers';
import { SiteRole } from '@/components/LiveMap/liveMapModel';

// Grouped by what trucks do at the site, so a dispatcher can spot jobs to chain
// (e.g. a truck that dumps here for one job can load here for another)
const ROLE_GROUPS: { role: SiteRole; title: string }[] = [
  { role: 'load', title: 'Loads here' },
  { role: 'dump', title: 'Dumps here' },
  { role: 'stop', title: 'Stops here' },
];

/**
 * Site name, address, trucks inside its geofence right now, and every job of
 * the day that uses the site, whether or not it is shown on the map. Jobs can
 * be pinned one after another (or all at once) without the list shrinking, so
 * several jobs through the same site can be compared.
 * @param {object} props
 * @param {SiteMarkerData} props.data - The site, the day's jobs using it and,
 *   on future days, last week's jobs using it.
 * @param {number | null} props.truckCount - Live trucks inside the site's
 *   geofence; null on days without live positions.
 * @param {string[]} props.pinnedJobIds
 * @param {(jobId: string) => void} props.onTogglePin
 * @param {(jobIds: string[], isPinned: boolean) => void} props.onSetJobsPinned
 * @returns {React.JSX.Element}
 */
export default function SiteDetails({
  data,
  truckCount,
  pinnedJobIds,
  onTogglePin,
  onSetJobsPinned,
}: {
  data: SiteMarkerData;
  truckCount: number | null;
  pinnedJobIds: string[];
  onTogglePin: (jobId: string) => void;
  onSetJobsPinned: (jobIds: string[], isPinned: boolean) => void;
}) {
  const { site, entries, contextEntries } = data;
  const jobIds = entries.map(({ job }) => job.id);
  const pinnedCount = jobIds.filter((jobId) =>
    pinnedJobIds.includes(jobId)
  ).length;
  const areAllPinned = pinnedCount === jobIds.length;
  return (
    <div className="site-details text-base-content w-64 space-y-2 text-sm">
      <div>
        <div className="text-xs font-bold tracking-wide uppercase opacity-60">
          {'Site'}
        </div>
        <div className="text-base leading-tight font-bold">{site.name}</div>
        {!!site.address && site.address !== site.name && (
          <div className="text-xs opacity-70">{site.address}</div>
        )}
      </div>
      {truckCount !== null && (
        <div className="bg-base-200 rounded-md px-2 py-1.5 font-semibold">
          {truckCount === 0
            ? 'No trucks on site right now'
            : `${truckCount} ${truckCount === 1 ? 'truck' : 'trucks'} on site right now`}
        </div>
      )}
      {entries.length > 1 && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="opacity-70">
            {`${entries.length} jobs this day · ${pinnedCount} pinned`}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-xs text-accent"
            onClick={() => onSetJobsPinned(jobIds, !areAllPinned)}
          >
            {areAllPinned ? 'Unpin all' : 'Pin all'}
          </button>
        </div>
      )}
      <div className="space-y-2">
        {ROLE_GROUPS.map(({ role, title }) => {
          const groupEntries = entries.filter(
            ({ site: jobSite }) => jobSite.role === role
          );
          if (!groupEntries.length) return null;
          return (
            <div key={role}>
              <div className="mb-0.5 text-xs font-bold tracking-wide uppercase opacity-60">
                {`${title} (${groupEntries.length})`}
              </div>
              <ul className="space-y-0.5">
                {groupEntries.map(({ job, site: jobSite }) => (
                  <li key={`${job.id}-${jobSite.label}`}>
                    <PopupJobRow
                      jobId={job.id}
                      job={job}
                      subtitle={[
                        jobSite.label,
                        job.startDatetime.getTimeStr(),
                        job.clientName,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      isPinned={pinnedJobIds.includes(job.id)}
                      onTogglePin={onTogglePin}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {!!contextEntries.length && (
        <RecentJobsAtSite contextEntries={contextEntries} />
      )}
    </div>
  );
}

const ROLE_SHORT_LABELS: Record<SiteRole, string> = {
  load: 'Load',
  dump: 'Dump',
  stop: 'Stop',
};

/**
 * Last week's jobs that used the site (future days only). Not pinnable, since
 * they aren't on the day shown, but each opens its details in a new tab.
 * @param {object} props
 * @param {SiteMarkerData['contextEntries']} props.contextEntries
 * @returns {React.JSX.Element}
 */
function RecentJobsAtSite({
  contextEntries,
}: {
  contextEntries: SiteMarkerData['contextEntries'];
}) {
  const sorted = [...contextEntries].sort(
    (a, b) => b.job.startDatetime.getTime() - a.job.startDatetime.getTime()
  );
  return (
    <div>
      <div className="mb-0.5 text-xs font-bold tracking-wide uppercase opacity-60">
        {`Last 7 days (${sorted.length})`}
      </div>
      <ul className="space-y-0.5">
        {sorted.map(({ job, site: jobSite }) => (
          <li
            key={`${job.id}-${jobSite.label}`}
            className="flex items-start gap-2 rounded-md px-1.5 py-1"
          >
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 font-semibold">{job.name}</span>
              <span className="block text-xs opacity-70">
                {[
                  job.startDatetime.toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  }),
                  `${jobSite.label} · ${ROLE_SHORT_LABELS[jobSite.role]}`,
                  job.clientName,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            <JobDetailsLink jobId={job.id} jobName={job.name} />
          </li>
        ))}
      </ul>
    </div>
  );
}
